import React from "react";
import { ArrowDownIcon, CheckIcon, StarIcon } from "lucide-react";
import { Eyebrow } from "./Eyebrow";
import { processSteps, matterStages, completionChecklist } from "../data/process";
import { images } from "../data/images";
function StepText({
  index,
  dark = false
}: {
  index: number;
  dark?: boolean;
}) {
  const s = processSteps[index];
  return <div data-id="element-212" data-mp="23c7a5vbvix41">
      <p className={`text-[13px] font-semibold ${dark ? "text-accent" : "text-accent"}`} data-id="element-213" data-mp="23bxje5j08lxa">{s.step}</p>
      <h3 className={`mt-3 text-[24px] font-semibold leading-tight tracking-[-0.02em] ${dark ? "text-white" : "text-ink"}`} data-id="element-214" data-mp="16m7uzi17x0ch">
        {s.title}
      </h3>
      <p className={`mt-3 max-w-[340px] text-[15px] leading-[1.6] ${dark ? "text-white/65" : "text-stone"}`} data-id="element-215" data-mp="1fnxiwdvw5h5i">
        {s.description}
      </p>
    </div>;
}
export function Process() {
  return <section id="process" className="w-full bg-cream px-6 py-[140px] lg:px-20" data-id="element-216" data-mp="mza5t5lapy31">
      <div className="mx-auto max-w-page" data-id="element-217" data-mp="rycxloc9m7t2">
        <div data-reveal className="flex flex-wrap items-end justify-between gap-8" data-id="element-218" data-mp="rx1ulqqn9430">
          <div data-id="element-219" data-mp="19kpnokwh71zn">
            <Eyebrow data-id="element-220" data-mp="19nbv8kjd9hhv">Our process</Eyebrow>
            <h2 className="mt-6 text-[48px] font-semibold leading-[0.98] tracking-[-0.045em] text-ink lg:text-[80px]" data-id="element-221" data-mp="15j49mw87ghnv">
              How we work,
              <br data-id="element-222" data-mp="10r30vka8x92l" />
              <span className="text-stone" data-id="element-223" data-mp="10qkw1z9ipmj2">start to finish</span>
            </h2>
          </div>
          <div className="flex flex-col items-end gap-6" data-id="element-224" data-mp="vs022hkuzz32">
            <p className="max-w-[300px] text-right text-[15px] leading-[1.6] text-stone" data-id="element-225" data-mp="qvj30sc4g0b7">
              Direct advice, transparent communication and no unnecessary complexity.
            </p>
            <a href="#contact" aria-label="Skip to enquiry form" className="flex h-11 w-11 items-center justify-center rounded-full bg-ink text-white transition-colors duration-150 hover:bg-accent hover:text-ink" data-id="element-226" data-mp="hv3661pvvrfb">
              <ArrowDownIcon className="h-5 w-5" strokeWidth={1.75} data-id="element-227" data-mp="2799ckem0fgs8" />
            </a>
          </div>
        </div>

        <div className="mt-16 grid grid-cols-1 gap-5 md:grid-cols-3 md:grid-rows-[380px_380px]" data-id="element-228" data-mp="2qdcpxip3ljjt">
          {/* Step 01 — tall */}
          <article data-reveal className="flex flex-col overflow-hidden rounded-2xl bg-white md:row-span-2" data-id="element-229" data-mp="2v5w3wgrp6gp5">
            <div className="p-8" data-id="element-230" data-mp="2herbnzqnsqou">
              <StepText index={0} data-id="element-231" data-mp="2mbriwdz8vzhj" />
            </div>
            <img src={images.processConsult} alt="An advisor talking a client through their notes" className="mt-auto min-h-[300px] w-full flex-1 object-cover" data-id="element-232" data-mp="2mcj0ulzx5ypc" />
          </article>

          {/* Step 02 — tracker */}
          <article data-reveal data-reveal-delay="1" className="flex flex-col rounded-2xl bg-white p-8" data-id="element-233" data-mp="302cs1aywcv28">
            <StepText index={1} data-id="element-234" data-mp="351g0l5e35jey" />
            <ol className="mt-auto space-y-3 border-t border-line pt-5" aria-label="Example matter progress" data-id="element-235" data-mp="354lpajisyja5">
              {matterStages.map(stage => <li key={stage.label} className="flex items-center gap-3 text-[13px]" data-id="element-236" data-mp="1todho3oikhrm">
                  <span className={`flex h-5 w-5 items-center justify-center rounded-full ${stage.state === "done" ? "bg-ink text-white" : stage.state === "current" ? "border-2 border-accent" : "border border-line"}`} data-id="element-237" data-mp="1m31klx2bv210">
                    {stage.state === "done" && <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-238" data-mp="12gn4scxzpftz" />}
                    {stage.state === "current" && <span className="h-1.5 w-1.5 rounded-full bg-accent" data-id="element-239" data-mp="yftfpg8t9uzi" />}
                  </span>
                  <span className={stage.state === "upcoming" ? "text-stone" : "font-medium text-ink"} data-id="element-240" data-mp="1c77ypn2pxw6k">
                    {stage.label}
                  </span>
                  {stage.state === "current" && <span className="ml-auto text-[11px] font-semibold uppercase tracking-[0.12em] text-accent" data-id="element-241" data-mp="jnj9kn54iux9">
                      In progress
                    </span>}
                </li>)}
            </ol>
          </article>

          {/* Step 03 — image bottom */}
          <article data-reveal data-reveal-delay="2" className="flex flex-col overflow-hidden rounded-2xl bg-white" data-id="element-242" data-mp="x9yqwebl2hef">
            <div className="p-8 pb-6" data-id="element-243" data-mp="171w0xhquv3bk">
              <StepText index={2} data-id="element-244" data-mp="172xoumsyzkap" />
            </div>
            <div className="mt-auto px-3 pb-3" data-id="element-245" data-mp="173p7kx1gxnjk">
              <img src={images.editorial} alt="Advisors reviewing a matter plan with a client" className="h-[130px] w-full rounded-xl object-cover" data-id="element-246" data-mp="1b2g1kvan0p2g" />
            </div>
          </article>

          {/* Step 04 — wide dark */}
          <article data-reveal data-reveal-delay="3" className="relative grid grid-cols-1 overflow-hidden rounded-2xl bg-ink p-8 md:col-span-2 md:grid-cols-2" data-id="element-247" data-mp="1q109yqiecx38">
            <div className="flex flex-col" data-id="element-248" data-mp="3mvd2yo0u3809">
              <span className="self-start rounded-full border border-white/20 px-3 py-1 text-[11px] font-medium text-white/80" data-id="element-249" data-mp="3rt4v9j5e4w4z">
                Clarity guaranteed
              </span>
              <div className="mt-auto pt-8" data-id="element-250" data-mp="245qw6xnkz6mf">
                <StepText index={3} dark data-id="element-251" data-mp="244z96b81uh7r" />
              </div>
            </div>

            <div className="relative mt-8 flex items-end justify-center md:mt-0" data-id="element-252" data-mp="205gp1ho1ya5z">
              <div className="w-[280px] rotate-[-3deg] rounded-xl bg-cream p-6 shadow-[0_20px_40px_rgba(0,0,0,0.25)]" data-id="element-253" data-mp="1v6n97ilam72n">
                <div className="flex items-center justify-between" data-id="element-254" data-mp="2rvbdlc435vtl">
                  <p className="text-[18px] font-semibold leading-none tracking-[-0.02em] text-ink" data-id="element-255" data-mp="2ntzvdrnjkcvf">Matter summary</p>
                  <span className="text-[11px] font-semibold text-accent" data-id="element-256" data-mp="2itve3zet7e45">Closed</span>
                </div>
                <p className="mt-1.5 text-[12px] text-stone" data-id="element-257" data-mp="2dvk3vnklr1le">Commercial lease review · Ref NG-2481</p>
                <ul className="mt-5 space-y-3 border-t border-line pt-4" data-id="element-258" data-mp="17hkuaii1msbu">
                  {completionChecklist.map(item => <li key={item} className="flex items-center gap-2.5 text-[13px] font-medium text-ink" data-id="element-259" data-mp="12j9ja4g0ibs1">
                      <span className="flex h-4 w-4 items-center justify-center rounded-sm bg-ink text-white" data-id="element-260" data-mp="18x6odtl75pme">
                        <CheckIcon className="h-3 w-3" strokeWidth={2.5} data-id="element-261" data-mp="uxnrxy1r02px" />
                      </span>
                      {item}
                    </li>)}
                </ul>
              </div>
              <div className="absolute right-2 top-2 flex items-center gap-2 rounded-lg bg-white px-3 py-2" data-id="element-262" data-mp="l7cd3ggj0949">
                <span className="flex gap-0.5" aria-hidden="true" data-id="element-263" data-mp="pzcj9qn6vy9a">
                  {Array.from({
                  length: 5
                }).map((_, i) => <StarIcon key={i} className="h-3 w-3 fill-accent text-accent" strokeWidth={0} data-id="element-264" data-mp="c8qytxi37g9a" data-mpi={i} />)}
                </span>
                <span className="text-[12px] font-semibold text-ink" data-id="element-265" data-mp="286tojrg5rls">4.9</span>
              </div>
            </div>
          </article>
        </div>
      </div>
    </section>;
}